import React, { useState, useEffect, useCallback, useRef } from "react";
import JSZip from "jszip";
import { getCroppedImg, createImage } from "./canvasUtils";
import { Button } from "./Button";
import { detectFace, calculateAutoCrop, loadModels } from "./faceUtils";

// Icon Components
const UploadIcon = () => (
  <svg
    className="text-indigo-400 mb-2"
    style={{ width: "40px" }}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      d="M4 16l4.586-4.586a2 2 0 012.828 0 L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 00-2 2z"
    />
  </svg>
);

const JsonIcon = () => (
  <svg
    className="w-4 mr-1"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
    />
  </svg>
);

const VisualIcon = () => (
  <svg
    className="w-4 mr-1"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
    />
  </svg>
);

const DownloadIcon = () => (
  <svg
    style={{ width: "25px", height: "25px" }}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2.5"
      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
    />
  </svg>
);

const TrashIcon = () => (
  <svg
    style={{ width: "25px", height: "25px" }}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2.5"
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
);

const RightArrow = () => (
  <svg className="w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="3"
      d="M9 5l7 7-7 7"
    />
  </svg>
);

const LeftArrow = () => (
  <svg className="w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="3"
      d="M15 19l-7-7 7-7"
    />
  </svg>
);

const UpArrow = () => (
  <svg className="w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="3"
      d="M5 15l7-7 7 7"
    />
  </svg>
);

const DownArrow = () => (
  <svg className="w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="3"
      d="M19 9l-7 7-7-7"
    />
  </svg>
);

const RATIOS = [
  { label: "Custom", value: 0 },
  { label: "1:1", value: 1 },
  { label: "3:4", value: 0.75 },
  { label: "4:5", value: 0.8 },
  { label: "2:3", value: 0.666 }
];

const DEFAULT_SETTINGS = {
  zoom: 0.35,
  aspectRatio: 7 / 8.05,
  horizontalOffset: 0,
  verticalOffset: 0,
  outputWidthInches: 7,
  outputHeightInches: 8.05
};

const ConfigPanel = ({
  configPosition,
  setConfigPosition,
  configMode,
  setConfigMode,
  settings,
  setSettings,
  activeShortcut,
  setActiveShortcut,
  inputJson,
  setInputJson
}) => {
  const isBottom = configPosition === "bottom";

  return (
    <div
      className={`flex flex-col overflow-hidden bg-slate-100 rounded-xl border border-slate-200 ${
        isBottom ? "w-full" : "h-full"
      }`}
    >
      <div className="p-1.5 flex gap-1 shrink-0 border-b border-slate-200">
        <button
          onClick={() => setConfigMode("visual")}
          className={`flex-1 py-1.5 rounded-lg text-[10px] font-black uppercase flex items-center justify-center transition-all ${
            configMode === "visual"
              ? "bg-white text-indigo-600 shadow-sm"
              : "text-slate-500 hover:text-slate-700"
          }`}
        >
          <VisualIcon /> Visual
        </button>
        <button
          onClick={() => setConfigMode("json")}
          className={`flex-1 py-1.5 rounded-lg text-[10px] font-black uppercase flex items-center justify-center transition-all ${
            configMode === "json"
              ? "bg-white text-indigo-600 shadow-sm"
              : "text-slate-500 hover:text-slate-700"
          }`}
        >
          <JsonIcon /> JSON
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
        {configMode === "visual" ? (
          <div
            className={`bg-white p-4 flex-1 flex flex-col min-h-0 ${
              isBottom ? "space-y-0" : "space-y-4"
            }`}
          >
            <div
              className={`${
                isBottom ? "grid grid-cols-12 gap-6 items-center" : "space-y-4"
              }`}
            >
              <div className={`${isBottom ? "col-span-4" : ""} space-y-2`}>
                <div className="flex justify-between items-center mb-1">
                  <label className="text-[12px] font-black uppercase text-slate-500 block tracking-widest">
                    Ratio Preset
                  </label>
                  <div className="bg-slate-50 rounded-lg p-0.5 flex items-center border border-slate-100 origin-right">
                    <button
                      onClick={() => setConfigPosition("left")}
                      title="Left"
                      className={`p-1 rounded-md transition-all ${
                        configPosition === "left"
                          ? "bg-white text-indigo-600 shadow-sm"
                          : "text-slate-300 hover:text-slate-500"
                      }`}
                    >
                      <svg
                        className="w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2.5"
                          d="M11 19l-7-7 7-7M20 12H4"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={() => setConfigPosition("bottom")}
                      title="Bottom"
                      className={`p-1 rounded-md transition-all ${
                        configPosition === "bottom"
                          ? "bg-white text-indigo-600 shadow-sm"
                          : "text-slate-300 hover:text-slate-500"
                      }`}
                    >
                      <svg
                        className="w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2.5"
                          d="M19 13l-7 7-7-7m7 7V4"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={() => setConfigPosition("right")}
                      title="Right"
                      className={`p-1 rounded-md transition-all ${
                        configPosition === "right"
                          ? "bg-white text-indigo-600 shadow-sm"
                          : "text-slate-300 hover:text-slate-500"
                      }`}
                    >
                      <svg
                        className="w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2.5"
                          d="M13 5l7 7-7 7M4 12h16"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-5 gap-1.5">
                  {RATIOS.map((r) => (
                    <button
                      key={r.label}
                      onClick={() => {
                        if (r.value > 0)
                          setSettings({
                            ...settings,
                            aspectRatio: r.value,
                            outputWidthInches: r.value >= 1 ? 7 : 7 * r.value,
                            outputHeightInches: r.value >= 1 ? 7 / r.value : 7
                          });
                        else setSettings({ ...settings, aspectRatio: 0 });
                      }}
                      className={`py-2 rounded-md text-[10px] font-black border transition-all ${
                        settings.aspectRatio === r.value
                          ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                          : "bg-white text-slate-600 border-slate-100 hover:border-slate-300"
                      }`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className={`${isBottom ? "col-span-3" : ""} space-y-1.5`}>
                <label className="text-[10px] font-black uppercase text-slate-400 block tracking-widest">
                  Dimensions (Inches)
                </label>
                <div className="grid grid-cols-3 gap-1.5">
                  <input
                    type="number"
                    step="0.1"
                    value={settings.outputWidthInches}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        outputWidthInches: parseFloat(e.target.value),
                        aspectRatio: 0
                      })
                    }
                    className="w-full bg-slate-50 border border-slate-200 rounded-md px-1.5 py-1 text-[11px] font-bold text-slate-700 outline-none"
                    title="Width"
                  />
                  <input
                    type="number"
                    step="0.1"
                    value={settings.outputHeightInches}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        outputHeightInches: parseFloat(e.target.value),
                        aspectRatio: 0
                      })
                    }
                    className="w-full bg-slate-50 border border-slate-200 rounded-md px-1.5 py-1 text-[11px] font-bold text-slate-700 outline-none"
                    title="Height"
                  />
                  <div className="bg-indigo-50/50 flex flex-col justify-center items-center rounded-md">
                    <span className="text-[12px] font-mono font-black text-indigo-600 leading-none">
                      {Math.round(settings.outputWidthInches * 300)} ×
                      {Math.round(settings.outputHeightInches * 300)}
                    </span>
                  </div>
                </div>
              </div>

              <div
                className={`${
                  isBottom ? "col-span-5" : ""
                } flex flex-col space-y-2`}
              >
                <div
                  className={`p-2 rounded-lg transition-all cursor-pointer border border-transparent ${
                    activeShortcut === "zoom"
                      ? "bg-indigo-50 border-indigo-100 shadow-sm"
                      : "hover:bg-slate-50"
                  }`}
                  onClick={() => setActiveShortcut("zoom")}
                >
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-[11px] font-black text-slate-900 uppercase flex items-center">
                      Zoom (Z)
                      <span className="inline-flex items-center ml-1 opacity-60 gap-0.5">
                        <UpArrow />
                        <DownArrow />
                      </span>
                    </span>
                    <span className="text-indigo-600 font-black text-[11px]">
                      {Math.round(settings.zoom * 100)}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0.15"
                    max="0.55"
                    step="0.01"
                    value={settings.zoom}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        zoom: parseFloat(e.target.value)
                      })
                    }
                    className="w-full mt-5 h-1 bg-slate-200 rounded-lg appearance-none accent-indigo-600 cursor-pointer"
                  />
                </div>

                <div
                  className={`p-2 rounded-lg transition-all cursor-pointer border border-transparent ${
                    activeShortcut === "verticalOffset"
                      ? "bg-indigo-50 border-indigo-100 shadow-sm"
                      : "hover:bg-slate-50"
                  }`}
                  onClick={() => setActiveShortcut("verticalOffset")}
                >
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-[11px] font-black text-slate-900 uppercase flex items-center">
                      V-Shift (X)
                      <span className="inline-flex items-center ml-1 opacity-60 gap-0.5">
                        <UpArrow />
                        <DownArrow />
                      </span>
                    </span>
                    <span className="text-indigo-600 font-black text-[11px]">
                      {Math.round(settings.verticalOffset * 100)}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min="-0.5"
                    max="0.5"
                    step="0.01"
                    value={settings.verticalOffset}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        verticalOffset: parseFloat(e.target.value)
                      })
                    }
                    className="w-full mt-5 h-1 bg-slate-200 rounded-lg appearance-none accent-indigo-600 cursor-pointer"
                  />
                </div>

                <div
                  className={`p-2 rounded-lg transition-all cursor-pointer border border-transparent ${
                    activeShortcut === "horizontalOffset"
                      ? "bg-indigo-50 border-indigo-100 shadow-sm"
                      : "hover:bg-slate-50"
                  }`}
                  onClick={() => setActiveShortcut("horizontalOffset")}
                >
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-[11px] font-black text-slate-900 uppercase flex items-center">
                      H-Shift (W)
                      <span className="inline-flex items-center ml-1 opacity-60 gap-0.5">
                        <LeftArrow />
                        <RightArrow />
                      </span>
                    </span>
                    <span className="text-indigo-600 font-black text-[11px]">
                      {Math.round(settings.horizontalOffset * 100)}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min="-0.5"
                    max="0.5"
                    step="0.01"
                    value={settings.horizontalOffset}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        horizontalOffset: parseFloat(e.target.value)
                      })
                    }
                    className="w-full mt-5 h-1 bg-slate-200 rounded-lg appearance-none accent-indigo-600 cursor-pointer"
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-slate-900 flex-1 flex flex-col min-h-0">
            <textarea
              value={inputJson}
              onChange={(e) => setInputJson(e.target.value)}
              className="w-full flex-1 bg-slate-950 text-emerald-400 font-mono text-[9px] p-3 outline-none resize-none"
              spellCheck={false}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const CornerHandle = ({ position, onMouseDown }) => {
  const posClasses = {
    tl: "-top-6 -left-6 cursor-nwse-resize",
    tr: "-top-6 -right-6 cursor-nesw-resize",
    bl: "-bottom-6 -left-6 cursor-nesw-resize",
    br: "-bottom-6 -right-6 cursor-nwse-resize"
  };

  const asd = { position: "absolute" };

  const svgs = {
    tl: (
      <path
        d="M14 2H2V14"
        stroke="white"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ),
    tr: (
      <path
        d="M2 2H14V14"
        stroke="white"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ),
    bl: (
      <path
        d="M14 14H2V2"
        stroke="white"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ),
    br: (
      <path
        d="M2 14H14V2"
        stroke="white"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    )
  };

  return (
    <div
      onMouseDown={onMouseDown}
      className={`h-12 w-12 z-30 pointer-events-auto flex items-center justify-center group ${posClasses[position]}`}
      style={asd}
    >
      <svg
        className="w-6 transition-all duration-200 group-hover:scale-125 group-hover:drop-shadow-[0_0_8px_rgba(255,255,255,0.8)] drop-shadow-[0_1px_4px_rgba(0,0,0,0.8)]"
        viewBox="0 0 16 16"
        fill="none"
      >
        {svgs[position]}
      </svg>
    </div>
  );
};

const aiCropper = () => {
  const [images, setImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isConfiguring, setIsConfiguring] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [configMode, setConfigMode] = useState("visual");
  const [configPosition, setConfigPosition] = useState("left");
  const [workspaceZoom, setWorkspaceZoom] = useState(1.0);
  const [showQuitConfirm, setShowQuitConfirm] = useState(false);
  const [viewfinderVisualHeight, setViewfinderVisualHeight] = useState(65);
  const [sessionResults, setSessionResults] = useState([]);
  const [batchResultJson, setBatchResultJson] = useState(null);

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  const [inputJson, setInputJson] = useState("");
  const [sampleDetection, setSampleDetection] = useState(null);
  const [sampleSize, setSampleSize] = useState({ w: 0, h: 0 });
  const [activeShortcut, setActiveShortcut] = useState(null);

  const isDragging = useRef(false);
  const isResizing = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const viewportRef = useRef(null);

  useEffect(() => {
    loadModels().catch(console.error);
  }, []);

  const reset = useCallback(() => {
    setImages([]);
    setCurrentIndex(0);
    setSessionResults([]);
    setIsFinished(false);
    setIsProcessing(false);
    setIsConfiguring(false);
    setBatchResultJson(null);
    setActiveShortcut(null);
    setShowQuitConfirm(false);
    setViewfinderVisualHeight(65);
    setSettings(DEFAULT_SETTINGS);
  }, []);

  useEffect(() => {
    if (configMode === "visual") {
      setInputJson(JSON.stringify(settings, null, 2));
    }
  }, [settings, configMode]);

  useEffect(() => {
    if (isConfiguring && images[currentIndex]) {
      const updateSample = async () => {
        const img = await createImage(images[currentIndex].preview);
        const detection = await detectFace(img);

        if (detection) {
          setSampleDetection(detection);
        } else {
          // Synthetic fallback: a box in the center of the image
          const boxSize = Math.min(img.width, img.height) * 0.25;
          setSampleDetection({
            box: {
              x: (img.width - boxSize) / 2,
              y: (img.height - boxSize) / 2,
              width: boxSize,
              height: boxSize
            },
            isSynthetic: true
          });
        }
        setSampleSize({ w: img.width, h: img.height });
      };
      updateSample();
    }
  }, [currentIndex, isConfiguring, images]);

  const finalizeResults = useCallback((results) => {
    const finalFiltered = results.filter((r) => r.status !== "skipped");
    setBatchResultJson(JSON.stringify(finalFiltered, null, 2));
    setIsProcessing(false);
    setIsFinished(true);
    setIsConfiguring(false);
    setShowQuitConfirm(false);
  }, []);

  const completeBatch = useCallback(
    async (manualResults, targetSettings, currentImageList) => {
      setIsProcessing(true);
      const manualMap = new Map(manualResults.map((r) => [r.id, r]));
      const imagesToProcess = currentImageList.filter(
        (img) => !manualMap.has(img.id)
      );

      const autoResults = [];
      if (imagesToProcess.length > 0) {
        const targetW = targetSettings.outputWidthInches * 300;
        const targetH = targetSettings.outputHeightInches * 300;
        const currentAspect =
          targetSettings.outputWidthInches / targetSettings.outputHeightInches;

        for (let i = 0; i < imagesToProcess.length; i++) {
          const item = imagesToProcess[i];
          setProgress({ current: i + 1, total: imagesToProcess.length });
          try {
            const imgElement = await createImage(item.preview);
            let detection = await detectFace(imgElement);

            if (!detection) {
              // Fallback synthetic box for batch auto-processing
              const boxSize =
                Math.min(imgElement.width, imgElement.height) * 0.25;
              detection = {
                box: {
                  x: (imgElement.width - boxSize) / 2,
                  y: (imgElement.height - boxSize) / 2,
                  width: boxSize,
                  height: boxSize
                }
              };
            }

            const pixelCrop = calculateAutoCrop(
              detection,
              imgElement.width,
              imgElement.height,
              targetSettings.zoom,
              currentAspect,
              targetSettings.horizontalOffset,
              targetSettings.verticalOffset
            );
            const croppedUrl = await getCroppedImg(
              item.preview,
              pixelCrop,
              targetW,
              targetH,
              item.file.type
            );
            autoResults.push({
              id: item.id,
              originalName: item.file.name,
              status: "success",
              dataUrl: croppedUrl
            });
          } catch (err) {
            autoResults.push({
              id: item.id,
              originalName: item.file.name,
              status: "error",
              reason: String(err)
            });
          }
        }
      }

      const finalResults = currentImageList.map((img) => {
        return (
          manualMap.get(img.id) || autoResults.find((r) => r.id === img.id)
        );
      });

      finalizeResults(finalResults);
    },
    [finalizeResults]
  );

  const handleRestartProject = useCallback(() => {
    if (!batchResultJson) return;
    const currentResults = JSON.parse(batchResultJson);
    const remainingIds = new Set(currentResults.map((r) => r.id));

    const remainingImages = images.filter((img) => remainingIds.has(img.id));

    if (remainingImages.length === 0) {
      reset();
      return;
    }

    setImages(remainingImages);
    setCurrentIndex(0);
    setSessionResults([]);
    setIsFinished(false);
    setIsProcessing(false);
    setIsConfiguring(true);
    setBatchResultJson(null);
    setActiveShortcut(null);
    setShowQuitConfirm(false);
    setViewfinderVisualHeight(65);
  }, [batchResultJson, images, reset]);

  const handleCropIndividual = useCallback(async () => {
    if (!isConfiguring || isProcessing || images.length === 0) return;
    const item = images[currentIndex];
    const targetW = settings.outputWidthInches * 300;
    const targetH = settings.outputHeightInches * 300;
    const currentAspect =
      settings.outputWidthInches / settings.outputHeightInches;

    let result;
    try {
      if (sampleDetection) {
        const pixelCrop = calculateAutoCrop(
          sampleDetection,
          sampleSize.w,
          sampleSize.h,
          settings.zoom,
          currentAspect,
          settings.horizontalOffset,
          settings.verticalOffset
        );
        const croppedUrl = await getCroppedImg(
          item.preview,
          pixelCrop,
          targetW,
          targetH,
          item.file.type
        );
        result = {
          id: item.id,
          originalName: item.file.name,
          status: "success",
          dataUrl: croppedUrl
        };
      } else {
        // This case should theoretically not happen now because updateSample provides synthetic detection
        result = {
          id: item.id,
          originalName: item.file.name,
          status: "skipped",
          reason: "No face detected and no fallback"
        };
      }
    } catch (err) {
      result = {
        id: item.id,
        originalName: item.file.name,
        status: "error",
        reason: String(err)
      };
    }

    const updatedManualResults = [...sessionResults];
    const existingIdx = updatedManualResults.findIndex(
      (r) => r.id === result.id
    );
    if (existingIdx > -1) updatedManualResults[existingIdx] = result;
    else updatedManualResults.push(result);

    setSessionResults(updatedManualResults);

    if (currentIndex < images.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      completeBatch(updatedManualResults, settings, images);
    }
  }, [
    isConfiguring,
    isProcessing,
    images,
    currentIndex,
    settings,
    sampleDetection,
    sampleSize,
    sessionResults,
    completeBatch
  ]);

  const handleSkipIndividual = useCallback(() => {
    if (!isConfiguring || isProcessing || images.length === 0) return;

    const currentItem = images[currentIndex];
    const newImages = images.filter((img) => img.id !== currentItem.id);

    const newSessionResults = sessionResults.filter(
      (r) => r.id !== currentItem.id
    );
    setSessionResults(newSessionResults);
    setImages(newImages);

    if (newImages.length === 0) {
      reset();
      return;
    }

    if (currentIndex >= newImages.length) {
      completeBatch(newSessionResults, settings, newImages);
    }
  }, [
    isConfiguring,
    isProcessing,
    images,
    currentIndex,
    sessionResults,
    completeBatch,
    settings,
    reset
  ]);

  const handleQuitIndividual = useCallback(() => {
    if (!isConfiguring || isProcessing) return;
    setShowQuitConfirm(true);
  }, [isConfiguring, isProcessing]);

  const confirmQuit = useCallback(() => {
    completeBatch(sessionResults, settings, images);
  }, [sessionResults, settings, images, completeBatch]);

  const handleStartFullBatch = async () => {
    let finalParams = settings;
    if (configMode === "json") {
      try {
        finalParams = JSON.parse(inputJson);
      } catch (e) {
        alert("Invalid JSON parameters");
        return;
      }
    }
    completeBatch(sessionResults, finalParams, images);
  };

  const handleMouseDown = (e) => {
    if (!sampleDetection) return;
    isDragging.current = true;
    isResizing.current = false;
    dragStart.current = { x: e.clientX, y: e.clientY };
  };

  const handleCornerMouseDown = (e) => {
    if (!sampleDetection) return;
    e.stopPropagation();
    isResizing.current = true;
    isDragging.current = false;
    dragStart.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (!sampleDetection || !viewportRef.current) return;

      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;
      dragStart.current = { x: e.clientX, y: e.clientY };

      const viewfinder = viewportRef.current.querySelector(".viewfinder-frame");
      if (!viewfinder) return;
      const rect = viewfinder.getBoundingClientRect();

      if (isDragging.current) {
        const hShiftDelta = dx / rect.width;
        const vShiftDelta = dy / rect.height;

        setSettings((prev) => ({
          ...prev,
          horizontalOffset: Math.min(
            0.5,
            Math.max(-0.5, prev.horizontalOffset - hShiftDelta)
          ),
          verticalOffset: Math.min(
            0.5,
            Math.max(-0.5, prev.verticalOffset - vShiftDelta)
          )
        }));
      } else if (isResizing.current) {
        const centerY = rect.top + rect.height / 2;
        const oldDist = Math.abs(dragStart.current.y - dy - centerY);
        const newDist = Math.abs(e.clientY - centerY);
        const visualDelta = (newDist - oldDist) / (rect.height / 2);
        const newVisualHeight = Math.max(
          20,
          Math.min(95, viewfinderVisualHeight * (1 + visualDelta))
        );
        const newZoom =
          (viewfinderVisualHeight * settings.zoom) / newVisualHeight;

        if (newZoom >= 0.1 && newZoom <= 0.6) {
          setViewfinderVisualHeight(newVisualHeight);
          setSettings((prev) => ({ ...prev, zoom: newZoom }));
        }
      }
    },
    [sampleDetection, settings, viewfinderVisualHeight]
  );

  const handleMouseUp = () => {
    isDragging.current = false;
    isResizing.current = false;
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      const target = e.target;
      const isTextEntryFocused =
        (target.tagName === "INPUT" && target.type !== "range") ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable;

      if (isTextEntryFocused) return;
      if (!isConfiguring || isProcessing || showQuitConfirm) return;

      const key = e.key.toLowerCase();
      if (key === "z") {
        e.preventDefault();
        setActiveShortcut("zoom");
      } else if (key === "w") {
        e.preventDefault();
        setActiveShortcut("horizontalOffset");
      } else if (key === "x") {
        e.preventDefault();
        setActiveShortcut("verticalOffset");
      }

      if (key === "c") {
        e.preventDefault();
        handleCropIndividual();
      } else if (key === "s") {
        e.preventDefault();
        handleSkipIndividual();
      } else if (key === "q") {
        e.preventDefault();
        handleQuitIndividual();
      }

      if (activeShortcut === "zoom") {
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setSettings((s) => ({
            ...s,
            zoom: Math.min(0.55, Number((s.zoom + 0.01).toFixed(2)))
          }));
        } else if (e.key === "ArrowDown") {
          e.preventDefault();
          setSettings((s) => ({
            ...s,
            zoom: Math.max(0.15, Number((s.zoom - 0.01).toFixed(2)))
          }));
        }
      } else if (activeShortcut === "horizontalOffset") {
        if (e.key === "ArrowRight") {
          e.preventDefault();
          setSettings((s) => ({
            ...s,
            horizontalOffset: Math.min(
              0.5,
              Number((s.horizontalOffset + 0.01).toFixed(2))
            )
          }));
        } else if (e.key === "ArrowLeft") {
          e.preventDefault();
          setSettings((s) => ({
            ...s,
            horizontalOffset: Math.max(
              -0.5,
              Number((s.horizontalOffset - 0.01).toFixed(2))
            )
          }));
        }
      } else if (activeShortcut === "verticalOffset") {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setSettings((s) => ({
            ...s,
            verticalOffset: Math.min(
              0.5,
              Number((s.verticalOffset + 0.01).toFixed(2))
            )
          }));
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          setSettings((s) => ({
            ...s,
            verticalOffset: Math.max(
              -0.5,
              Number((s.verticalOffset - 0.01).toFixed(2))
            )
          }));
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    isConfiguring,
    isProcessing,
    activeShortcut,
    showQuitConfirm,
    handleCropIndividual,
    handleSkipIndividual,
    handleQuitIndividual
  ]);

  const handleDeleteResult = (id) => {
    if (batchResultJson) {
      const currentResults = JSON.parse(batchResultJson);
      const filtered = currentResults.filter((r) => r.id !== id);
      setBatchResultJson(JSON.stringify(filtered, null, 2));

      // If we emptied the results, let's just go back to start or keep the empty view
      if (filtered.length === 0) {
        setBatchResultJson(null);
        setIsFinished(false);
      }
    }
  };

  const handleDownloadAll = async () => {
    if (!batchResultJson) return;
    const results = JSON.parse(batchResultJson);
    const successfulResults = results.filter(
      (r) => r.status === "success" && r.dataUrl
    );

    if (successfulResults.length === 0) return;

    const zip = new JSZip();

    const MONTH_NAMES = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec"
    ];
    const now = new Date();
    const month = MONTH_NAMES[now.getMonth()];
    const day = String(now.getDate()).padStart(2, "0");
    const year = String(now.getFullYear()).slice(-2);
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");
    const timestampName = `cropped_${month}-${day}-${year}-${hours}-${minutes}-${seconds}`;

    successfulResults.forEach((res) => {
      const base64Data = res.dataUrl.split(",")[1];
      zip.file(res.originalName, base64Data, { base64: true });
    });

    const content = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(content);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${timestampName}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // After downloading all, clear the finished list
    reset();
  };

  const handleDownloadSingle = (id, dataUrl, fileName) => {
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Automatically remove from preview once downloaded
    handleDeleteResult(id);
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      setImages(
        files.map((file) => ({
          id: Math.random().toString(36).substr(2, 9),
          file,
          preview: URL.createObjectURL(file),
          status: "pending"
        }))
      );
      setCurrentIndex(0);
      setSessionResults([]);
      setIsConfiguring(true);
      setIsFinished(false);
      setBatchResultJson(null);
      setViewfinderVisualHeight(65);
    }
  };

  const handleDownloadCSS = () => {
    let cssContent = "/* ProCrop Studio Generated Styles */\n\n";

    // Scrape all style elements including custom and Tailwind-generated ones
    const styleElements = document.getElementsByTagName("style");
    for (let i = 0; i < styleElements.length; i++) {
      const style = styleElements[i];
      if (style.textContent) {
        cssContent += `/* Source: ${style.id || "Style Block " + (i + 1)} */\n`;
        cssContent += style.textContent + "\n\n";
      }
    }

    const blob = new Blob([cssContent], { type: "text/css" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "procrop-studio-styles.css";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getPreviewStyles = () => {
    if (!sampleDetection || !sampleSize.w) return {};
    const currentAspect =
      settings.outputWidthInches / settings.outputHeightInches;
    const crop = calculateAutoCrop(
      sampleDetection,
      sampleSize.w,
      sampleSize.h,
      settings.zoom,
      currentAspect,
      settings.horizontalOffset,
      settings.verticalOffset
    );
    const scale = 1 / (crop.width / sampleSize.w);
    const leftPercent = -(crop.x / sampleSize.w) * scale * 100;
    const topPercent =
      -(crop.y / sampleSize.h) *
      (sampleSize.h / (sampleSize.w / currentAspect)) *
      scale *
      100;

    return {
      left: `${leftPercent}%`,
      top: `${topPercent}%`,
      width: `${scale * 100}%`,
      height: "auto"
    };
  };

  return (
    <div className="h-screen overflow-hidden bg-slate-50 flex flex-col font-sans text-slate-900">
      <header className="bg-white border-b border-slate-200 px-4 py-2 flex items-center justify-between shrink-0 z-50">
        <div className="flex items-center space-x-2">
          <div
            className="bg-indigo-600 rounded-lg flex items-center justify-center shadow-md cursor-pointer"
            style={{ width: "30px", height: "30px" }}
            onClick={reset}
          >
            <svg
              className="text-white"
              style={{ width: "20px", height: "20px" }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.5"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
          </div>
          <span className="text-base font-black tracking-tight">
            ComposzCrop <span className="text-indigo-600">Studio</span>
          </span>
          {/* <button
            onClick={handleDownloadCSS}
            className="ml-4 px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 text-[10px] font-black rounded-lg border border-slate-200 transition-all flex items-center gap-1.5 shadow-sm active:scale-95"
            title="Export Compiled CSS Styles"
          >
            <svg
              className="w-3.5 h-3.5 text-indigo-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.5"
                d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
              />
            </svg>
            Create CSS
          </button> */}
        </div>
        <div className="text-[12px] font-bold text-slate-400">
          Tip: Drag image to pan • Drag handles to zoom • Use shortcuts (Z, X,
          W)
        </div>
      </header>

      <main className="flex-1 min-h-0 flex flex-col p-2.5 max-w-[1800px] mx-auto w-full overflow-hidden">
        {!isProcessing && !isFinished && !isConfiguring && (
          <div className="flex-1 flex items-center justify-center">
            <div
              className="w-full max-w-lg text-center space-y-4"
              style={{ position: "absolute", marginTop: "-10%" }}
            >
              <h2 className="text-2xl font-black text-slate-900 leading-tight">
                Batch Crop Studio
              </h2>
              <div className="bg-white border-2 border-dashed border-indigo-200 rounded-2xl p-8 transition-all hover:border-indigo-400 group relative cursor-pointer shadow-lg">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                />
                <div
                  className="flex flex-col items-center"
                  style={{ padding: "5%" }}
                >
                  <UploadIcon />
                  <div className="bg-indigo-600 text-white px-5 py-2 rounded-lg text-xs font-bold shadow-md">
                    Upload Images
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {isConfiguring && !isProcessing && images.length > 0 && (
          <div
            className={`flex gap-2.5 ${
              configPosition === "bottom"
                ? "flex-col"
                : configPosition === "left"
                ? "flex-row-reverse"
                : "flex-row"
            }`}
            style={{ height: "85vh" }}
          >
            <div className="flex-1 min-h-0 flex flex-col space-y-2 overflow-hidden">
              <div className="flex justify-between items-center bg-white px-3 py-1.5 rounded-xl shadow-sm border border-slate-100 shrink-0">
                <div className="flex flex-col">
                  <div className="flex items-center space-x-2">
                    <h2 className="text-[10px] font-black text-slate-900 uppercase">
                      Reviewing {currentIndex + 1} / {images.length}
                    </h2>
                    {sampleDetection && sampleDetection.isSynthetic && (
                      <span className="bg-amber-100 text-amber-700 text-[8px] px-1.5 py-0.5 rounded font-black uppercase tracking-widest border border-amber-200 animate-pulse">
                        Manual Mode
                      </span>
                    )}
                  </div>
                  <p className="text-slate-400 text-[8px] font-bold truncate max-w-[250px]">
                    {images[currentIndex].file.name}
                  </p>
                </div>
                <div className="flex items-center space-x-2.5 bg-slate-50 px-2 py-1 rounded-lg border border-slate-100">
                  <span className="text-[8px] font-black uppercase text-slate-400">
                    UI Scale
                  </span>
                  <input
                    type="range"
                    min="0.5"
                    max="2.0"
                    step="0.1"
                    value={workspaceZoom}
                    onChange={(e) =>
                      setWorkspaceZoom(parseFloat(e.target.value))
                    }
                    className="h-1 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                    style={{ width: "90px" }}
                  />
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() =>
                      setCurrentIndex(Math.max(0, currentIndex - 1))
                    }
                    className="flex items-center space-x-1 px-2.5 py-1 bg-slate-100 hover:bg-slate-200 rounded-md disabled:opacity-30 transition-colors"
                    disabled={currentIndex === 0}
                  >
                    <svg
                      className="w-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2.5"
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                    <span className="text-[10px] font-bold">Prev</span>
                  </button>
                  <button
                    onClick={() =>
                      setCurrentIndex(
                        Math.min(images.length - 1, currentIndex + 1)
                      )
                    }
                    className="flex items-center space-x-1 px-2.5 py-1 bg-slate-100 hover:bg-slate-200 rounded-md disabled:opacity-30 transition-colors"
                    disabled={currentIndex === images.length - 1}
                  >
                    <span className="text-[10px] font-bold">Next</span>
                    <svg
                      className="w-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2.5"
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              <div
                ref={viewportRef}
                className="flex-1 bg-slate-900 rounded-2xl overflow-hidden border border-slate-300 relative flex items-center justify-center min-h-0 shadow-2xl cursor-grab active:cursor-grabbing select-none"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                <div
                  className="viewfinder-frame relative z-10 bg-white/5 border border-white/20 shadow-[0_0_0_9999px_rgba(15,23,42,0.85)] pointer-events-none"
                  style={{
                    aspectRatio: `${settings.outputWidthInches} / ${settings.outputHeightInches}`,
                    height: `${viewfinderVisualHeight * workspaceZoom}%`,
                    maxHeight: "95%",
                    maxWidth: "95%"
                  }}
                >
                  <div className="absolute inset-0 overflow-hidden bg-slate-950">
                    {sampleSize.w > 0 && (
                      <img
                        src={images[currentIndex].preview}
                        className="absolute max-w-none transition-none pointer-events-none"
                        style={getPreviewStyles()}
                      />
                    )}
                  </div>
                  <CornerHandle
                    position="tl"
                    onMouseDown={handleCornerMouseDown}
                  />
                  <CornerHandle
                    position="tr"
                    onMouseDown={handleCornerMouseDown}
                  />
                  <CornerHandle
                    position="bl"
                    onMouseDown={handleCornerMouseDown}
                  />
                  <CornerHandle
                    position="br"
                    onMouseDown={handleCornerMouseDown}
                  />

                  <div className="absolute inset-0 pointer-events-none opacity-20">
                    <div className="absolute top-1/3 w-full h-px bg-white"></div>
                    <div className="absolute top-2/3 w-full h-px bg-white"></div>
                    <div className="absolute left-1/3 h-full w-px bg-white"></div>
                    <div className="absolute left-2/3 h-full w-px bg-white"></div>
                  </div>
                </div>
              </div>

              <div className="flex space-x-2 shrink-0">
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleQuitIndividual}
                  className="flex-1 font-black uppercase tracking-wider text-[10px]"
                >
                  Finish & Export (Q)
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleSkipIndividual}
                  className="flex-1 font-black uppercase tracking-wider text-[10px]"
                >
                  Skip / Remove (S)
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleCropIndividual}
                  className="flex-[2] font-black uppercase tracking-widest text-[10px]"
                >
                  Crop & Next (C)
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleStartFullBatch}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 border-emerald-700 font-black uppercase tracking-wider text-[10px]"
                >
                  Auto-Batch Remaining
                </Button>
              </div>
            </div>

            <div
              className={`${
                configPosition === "bottom" ? "w-full h-48" : "w-72 h-full"
              } shrink-0`}
            >
              <ConfigPanel
                configPosition={configPosition}
                setConfigPosition={setConfigPosition}
                configMode={configMode}
                setConfigMode={setConfigMode}
                settings={settings}
                setSettings={setSettings}
                activeShortcut={activeShortcut}
                setActiveShortcut={setActiveShortcut}
                inputJson={inputJson}
                setInputJson={setInputJson}
              />
            </div>
          </div>
        )}

        {isProcessing && (
          <div className="flex-1 flex flex-col items-center justify-center space-y-6">
            <div className="relative w-24 h-24">
              <div className="absolute inset-0 border-8 border-slate-200 rounded-full"></div>
              <div className="absolute inset-0 border-8 border-indigo-600 rounded-full border-t-transparent animate-spin"></div>
            </div>
            <div className="text-center">
              <h2 className="text-xl font-black text-slate-900 mb-1">
                Processing Batch
              </h2>
              <p className="text-slate-500 font-bold text-sm">
                Working on {progress.current} of {progress.total} images...
              </p>
            </div>
          </div>
        )}

        {isFinished && (
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden space-y-4">
            <div className="flex items-center justify-between bg-white p-4 rounded-2xl shadow-sm border border-slate-100 shrink-0">
              <div>
                <h2 className="text-lg font-black text-slate-900 leading-tight">
                  Cropped Images Preview
                </h2>
                <p className="text-slate-400 text-xs font-bold">
                  Processed{" "}
                  {
                    JSON.parse(batchResultJson || "[]").filter(
                      (r) => r.status === "success"
                    ).length
                  }{" "}
                  headshots successfully
                </p>
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="primary"
                  onClick={handleDownloadAll}
                  icon={<DownloadIcon />}
                  className="font-black uppercase tracking-wider text-[11px] px-6"
                >
                  Download All (ZIP)
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleRestartProject}
                  className="font-black uppercase tracking-wider text-[11px] px-6 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                >
                  RE-CROP REMAINING
                </Button>
                <Button
                  variant="ghost"
                  onClick={reset}
                  className="font-black uppercase tracking-wider text-[11px] px-6 text-slate-400 hover:text-slate-600"
                >
                  NEW SESSION
                </Button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
              <div className="grid grid-cols-6 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
                {JSON.parse(batchResultJson || "[]")
                  .filter((res) => res.status === "success")
                  .map((res) => (
                    <div
                      key={res.id}
                      className="bg-white rounded-xl overflow-hidden border border-slate-200 shadow-sm transition-all hover:shadow-md group relative"
                    >
                      <div className="aspect-[3/4] bg-slate-100 relative overflow-hidden flex items-center justify-center">
                        <img
                          src={res.dataUrl}
                          className="w-full h-full object-cover"
                        />

                        <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-2 z-10">
                          <button
                            onClick={() =>
                              handleDownloadSingle(
                                res.id,
                                res.dataUrl,
                                res.originalName
                              )
                            }
                            className="rounded-full flex items-center justify-center bg-indigo-600 hover:bg-indigo-700 text-white shadow-xl transition-transform hover:scale-110"
                            style={{ width: "40px", height: "40px" }}
                            title="Download Image"
                          >
                            <DownloadIcon />
                          </button>
                          <button
                            onClick={() => handleDeleteResult(res.id)}
                            className="rounded-full flex items-center justify-center bg-red-600 hover:bg-red-700 text-white shadow-xl transition-transform hover:scale-110"
                            style={{ width: "40px", height: "40px" }}
                            title="Remove from list"
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      </div>
                      <div className="p-2 bg-white border-t border-slate-100">
                        <p
                          className="text-[9px] font-black text-slate-800 truncate mb-0.5"
                          title={res.originalName}
                        >
                          {res.originalName}
                        </p>
                        <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">
                          {Math.round(settings.outputWidthInches * 100) / 100}"
                          x{" "}
                          {Math.round(settings.outputHeightInches * 100) / 100}"
                        </p>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
      </main>

      {showQuitConfirm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center border border-slate-200">
            <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.5"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-black text-slate-900 mb-2">
              Finalize Batch?
            </h3>
            <p className="text-slate-500 text-sm font-medium mb-6">
              Finished reviewing? Any images you haven't manually cropped will
              be processed automatically using current settings.
            </p>
            <div className="flex space-x-3">
              <Button
                variant="secondary"
                onClick={() => setShowQuitConfirm(false)}
                className="flex-1 font-bold"
              >
                Wait, Go Back
              </Button>
              <Button
                variant="primary"
                onClick={confirmQuit}
                className="flex-1 font-black"
              >
                Finalize & Export
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default aiCropper;
